// ============================================================================
// ROLE HOOKS
// ============================================================================
// React hooks for the Role Master.

import { useState, useEffect, useCallback } from 'react';
import { roleService } from '../../api/services/roleService';
import type {
  Role,
  RoleListItem,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleSearchRequest,
} from '../../api/types/role.types';
import type { ID } from '../../api/types';

export const useRoles = (params?: RoleSearchRequest) => {
  const [data, setData] = useState<RoleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return { data, loading, error, pagination, refetch: fetchRoles };
};

export const useRole = (id: ID | undefined) => {
  const [data, setData] = useState<Role | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRole = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  return { data, loading, error, refetch: fetchRole };
};

export const useCreateRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRole = async (
    data: CreateRoleRequest,
    options?: { onSuccess?: (role: Role) => void; onError?: (error: Error) => void },
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createRole, loading, error };
};

export const useUpdateRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRole = async (
    id: ID,
    data: UpdateRoleRequest,
    options?: { onSuccess?: (role: Role) => void; onError?: (error: Error) => void },
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateRole, loading, error };
};

export const useDeleteRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRole = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void },
  ) => {
    setLoading(true);
    setError(null);
    try {
      await roleService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRole, loading, error };
};
